import TitleService from '../title-service'
import { ComponentUITypes, DefaultModelProperty, LabelValue } from './standard'

import { ProcessValidation, ValidationRule } from './validation-service'

export interface BuildFormArgs<M, P> {
  utilValidations: {
    [str: string]: ValidationRule[]
  }
  hasPosted: () => boolean
  isDirty: (property: P) => boolean
  page: any
  form: M
  context?: any
  setForm: (form: M, prop: P, formValid: boolean, updates?: any) => void
  GetPageProperties: (page: any, form: M) => DefaultModelProperty<P>[]
  IsRequired: (page: any, property: P, form: M) => boolean
  IsDisabled: (page: any, property: P, form: M) => boolean
  DropdownOptions: (page: any, property: P, value: M) => LabelValue<any>[]
  EnumOptions?: (page: any, property: P, value: M) => any[]
  OnEvents?: (eventName: string, value: M, property: P) => Promise<M>
  BuildList: (page: any, property: P, value: M) => JSX.Element[]
  IsVisible(page: any, property: P, value: M): boolean
  options?: any
}

export const formComponentTypes: {
  [str: string]: (args: any) => JSX.Element
} = {}

export interface ComponentGeneratorArgs<M, P> {
  title: string
  onClick: OnClick<M, P>
  required: boolean
  key: string
  form: any
  value: any
  disabled: boolean
  onChange: (val: any) => void
  onSelect: (val: any) => void
  enumOptions?: (page: any, property: P, value: M) => any[]
  invalid: boolean
  options: any
  list: any
}

export interface OnClick<M, P> {
  (): {
    form: M
    property: DefaultModelProperty<P>
  }
}

export function SetFormComponentTypes<M, P>(
  componentType: ComponentUITypes | string,
  generator: (args: ComponentGeneratorArgs<M, P>) => JSX.Element,
) {
  formComponentTypes[componentType] = generator
}

export function BuildForm<M, P extends keyof M>(formArgs: BuildFormArgs<M, P>) {
  let { page, form, GetPageProperties, IsVisible } = formArgs
  return GetPageProperties(page, form)
    .filter((x) => {
      return IsVisible(page, x.name, form)
    })
    .map((property) => {
      let validity = validateForm(formArgs, page, form, property)
      let generator = formComponentTypes[property.type]
      if (generator) {
        return generator({
          title: TitleService(property.defaultName),
          onClick: async () => {
            if (formArgs.OnEvents) {
              await formArgs.OnEvents('onClick', form, property.name)
            }
            return {
              form,
              property,
            }
          },
          required: formArgs.IsRequired(page, property.name, form),
          key: property.name,
          form,
          context: formArgs.context,
          propertyName: property.name,
          value: form ? form[property.name] : undefined,
          disabled: formArgs.IsDisabled(page, property.name, form),
          onChange: async (val: any, updates?: any) => {
            let updated_form = {
              ...form,
              ...(updates || {}),
              [property.name]: val,
            }
            if (formArgs.OnEvents) {
              updated_form = await formArgs.OnEvents(
                'onChange',
                updated_form,
                property.name,
              )
            }
            let formValid = !GetPageProperties(page, updated_form)
              .filter((x) => {
                return IsVisible(page, x.name, updated_form)
              })
              .some((property) => {
                return (
                  validateForm(formArgs, page, updated_form, property) !== false
                )
              })
            formArgs.setForm(updated_form, property.name, formValid, updates)
          },
          onSelect: async (val: any) => {
            let updated_form = {
              ...form,
              [property.name]: val,
            }
            if (formArgs.OnEvents) {
              updated_form = await formArgs.OnEvents(
                'onSelect',
                updated_form,
                property.name,
              )
            }
            let formValid = !GetPageProperties(page, updated_form)
              .filter((x) => {
                return IsVisible(page, x.name, updated_form)
              })
              .some((property) => {
                return (
                  validateForm(formArgs, page, updated_form, property) !== false
                )
              })
            formArgs.setForm(updated_form, property.name, formValid)
          },
          invalid: validity,
          options: formArgs.DropdownOptions(page, property.name, form),
          enumOptions:
            formArgs && formArgs.EnumOptions
              ? formArgs.EnumOptions(page, property.name, form)
              : null,
          list: formArgs.BuildList(page, property.name, form),
        })
      }
      return null
    })
}

export function validateForm<M, P extends keyof M>(
  options: BuildFormArgs<M, P>,
  page: any,
  form: M,
  property: DefaultModelProperty<P>,
  force?: boolean,
) {
  let validations = (form: M, name: DefaultModelProperty<P>) => {
    return ProcessValidation(form, name, options.utilValidations)
  }
  if (options && options.isDirty && options.hasPosted) {
    if (
      options.isDirty(property.name) ||
      options.hasPosted() ||
      force ||
      form
    ) {
      if (options.IsDisabled(page, property.name, form)) {
        return false
      }
      let validations_res = validations(form, property)
      let invalid = validations_res.invalid
      if (invalid) {
        return invalid ? validations_res : false
      }
    }
  }
  return false
}
